import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Toolbar,
  Typography
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import CircularLoading from "@saleor/autobooking/components/CircularLoading";
import ClockIcon from "@saleor/icons/ClockIcon";
import {
  MotherOrderList,
  MotherOrderList_orders_edges_node,
  MotherOrderListVariables
} from "@saleor/motherOrders/types/MotherOrderList";
import { ApolloQueryResult } from "apollo-client";
import React from "react";
import { useLocation } from "react-router-dom";

import RiderRefreshDialog from "../OrderDetailsPage/components/RiderRefreshDialog";

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      agingContainer: {
        alignItems: "center",
        color: "#C15669",
        display: "flex",
        fontSize: "13px",
        fontWeight: 600,
        justifyItems: "center"
      },
      appBar: {
        backgroundColor: "transparent",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.10)",
        color: "black",
        margin: 0,
        position: "relative"
      },
      assignButton: {
        "&:hover, &:active": {
          "& .MuiButton-label": {
            color: "#FFF"
          },
          backgroundColor: "#1848FE",
          borderColor: "#1848FE",
          color: "#FFF"
        },
        borderColor: "#1848FE",
        color: "#1848FE",
        textTransform: "none"
      },
      assignButtonActive: {
        "& .MuiButton-label": {
          color: "#FFF"
        },
        backgroundColor: "#1848FE",
        borderColor: "#1848FE",
        color: "#FFF"
      },

      dialogBoxContainer: {
        borderBottom: "1px solid #E8EAEE",
        display: "block"
      },
      dialogContainer: {
        height: "100vh",
        margin: 0,
        maxHeight: "100vh",
        overflow: "auto",
        position: "absolute",
        right: 0,
        top: 0
      },
      dialogContent: {
        padding: 0
      },
      orderCodeText: {
        fontWeight: 800
      },
      title: {
        flex: 1,
        marginLeft: theme.spacing(1)
      }
    }),
  { name: "UnassignedOrderListDialog" }
);

interface UnassignedOrderListDialogProps {
  unassignedOrders: MotherOrderList_orders_edges_node[];
  isModalOpen: boolean;
  loading: boolean;
  refetch: (
    variables?: MotherOrderListVariables
  ) => Promise<ApolloQueryResult<MotherOrderList>>;
  handleClose: () => void;
  onAssignRider: (
    orderId: string,
    orderCode: string,
    mallId: string,
    isFromNotification: boolean
  ) => void;
}

const UnassignedOrderListDialog: React.FC<UnassignedOrderListDialogProps> = props => {
  const {
    unassignedOrders,
    isModalOpen,
    loading,
    refetch,
    handleClose,
    onAssignRider
  } = props;

  const classes = useStyles(props);
  const location = useLocation();

  const dateToday = Date.now();

  const [cooldownDialogOpen, setCooldownDialogOpen] = React.useState(false);
  const [selectedOrderId, setSelectedOrderId] = React.useState("");

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isAssignModalOpen = params.get("action") === "onClickAssignRider";

    if (!isAssignModalOpen) {
      setSelectedOrderId("null");
    }
  }, [location.search]);

  const getDiffMinutes = (bookingTime: string, now: number) => {
    const bookingDate = new Date(bookingTime).getTime();
    if (isNaN(bookingDate)) {
      return null;
    }

    return Math.floor(Math.abs(now - bookingDate) / (1000 * 60));
  };

  const pluralForm = (value: number, unit: string) =>
    `${value}${unit}${value !== 1 ? "s" : ""}`;

  const agingFormat = minutes => {
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;

    if (minutes >= 1440) {
      return `${pluralForm(days, "Day")} ${pluralForm(
        hours,
        "Hour"
      )} ${pluralForm(mins, "Min")}`;
    } else if (minutes >= 60) {
      return `${pluralForm(hours, "Hour")} ${pluralForm(mins, "Min")}`;
    } else {
      return `${pluralForm(mins, "Min")}`;
    }
  };

  return (
    <>
      <RiderRefreshDialog
        orderId={selectedOrderId}
        open={cooldownDialogOpen}
        onClose={() => {
          setCooldownDialogOpen(false);
          refetch();
        }}
      />
      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          className: classes.dialogContainer
        }}
      >
        <AppBar className={classes.appBar}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              Notifications
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent className={classes.dialogContent}>
          {loading ? (
            <Box style={{ margin: "20px" }}>
              <CircularLoading />
            </Box>
          ) : (
            unassignedOrders
              ?.filter(order => order.status === "UNFULFILLED")
              .map((order, index) => (
                <Box
                  className={classes.dialogBoxContainer}
                  key={index}
                  py={1}
                  px={3}
                >
                  <Grid container spacing={1} alignItems="flex-start">
                    <Grid item xs={10}>
                      <Typography
                        className={classes.orderCodeText}
                        variant="body1"
                      >
                        {order?.orderCode}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Has no rider auto-booked yet
                      </Typography>
                      <Typography className={classes.agingContainer}>
                        <ClockIcon size={18} />
                        {agingFormat(
                          getDiffMinutes(order.bookingTime, dateToday)
                        )}
                        .
                      </Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        className={`${
                          classes.assignButton
                        } ${selectedOrderId === order.id &&
                          classes.assignButtonActive}`}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedOrderId(order.id);
                          onAssignRider(
                            order.id,
                            order.orderCode,
                            order?.mall?.id,
                            true
                          );
                        }}
                      >
                        Assign
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UnassignedOrderListDialog;
